import i18n from '@/utils/i18n';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { APP_ROUTES, ORDER_ROUTES } from '@/navigators/routes';
import { AppStackParams, OrdersStackParams } from '@/navigators/types';
import { CompositeNavigationProp } from '@react-navigation/core';
import { CreditRequestDetailComponent } from '@/screens/credit-request/credit-request-detail-component';
import {
  FilterOption,
  OrderHistoryFilters,
} from '@/components/orders/order-history-filters';
import { FocusSource, useComponentFocus } from '@/hooks/use-component-focus';
import { InvoiceDetailComponent } from '@/screens/invoice-detail/invoice-detail-component';
import { LIST_HEIGHT } from '@/components/horizontal-selector';
import { OfflineComponent } from '@/components/offline-component';
import { OrderDetailComponent } from '@/screens/order-detail/order-detail-component';
import { OrderHistoryList } from '@/components/orders/order-history-list';
import { OrdersEmptyState } from '@/components/orders/orders-empty-state';
import { Platform, RefreshControl, StyleSheet, Text, View } from 'react-native';
import { RouteProp } from '@react-navigation/native';
import {
  SCREEN_PADDING,
  SPLIT_CONTAINER_MARGIN_HORIZONTAL,
  TYPOGRAPHY,
} from '@/styles';
import {
  SplitPageContainer,
  useSharedStyles as useSplitContainerStyles,
} from '@/components/split-page-container';
import { StackNavigationProp } from '@react-navigation/stack';
import { SubscriptionButton } from '@/components/subscription-button';
import { useGetCreditRequestBatches } from '@/hooks/use-get-credit-request-batches';
import { useGetInvoicesWithoutOrders } from '@/hooks/use-get-invoices-without-orders';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useOrderHistoryListQuery } from '@/hooks/use-order-history-list-query';
import { usePaymentsContext } from '@/contexts/payments-context';
import { useUserContext } from '@/contexts/user-context';

const LIST_SECTION_HEADER_HEIGHT = 48;

export interface Props {
  navigation: CompositeNavigationProp<
    StackNavigationProp<OrdersStackParams, ORDER_ROUTES.Orders>,
    StackNavigationProp<AppStackParams>
  >;
  route: RouteProp<OrdersStackParams, ORDER_ROUTES.Orders>;
}

export const SplitOrdersScreen: React.FC<Props> = ({ navigation, route }) => {
  // Query hooks to fetch orders and invoices
  const {
    loading: loadingUpcomingOrders,
    orders: upcomingOrders,
    refresh: refreshUpcomingOrders,
    refreshing: refreshingUpcomingOrders,
  } = useOrderHistoryListQuery(false);
  const {
    fetchMore: fetchMorePastOrders,
    fetchingMore: fetchingMorePastOrders,
    loading: loadingPastOrders,
    orders: pastOrders,
    refresh: refreshPastOrders,
    refreshing: refreshingPastOrders,
  } = useOrderHistoryListQuery(true);

  // Fetch invoices without orders
  const {
    fetchMore: fetchMoreInvoices,
    fetchingMore: fetchingMoreInvoices,
    loading: loadingInvoices,
    refresh: refreshInvoices,
    refreshing: refreshingInvoices,
    pastInvoices,
    upcomingInvoices,
  } = useGetInvoicesWithoutOrders();

  const {
    creditRequests,
    fetchMore: fetchMoreCreditRequests,
    fetchingMore: fetchingMoreCreditRequests,
    loading: loadingCreditRequests,
    refresh: refreshCreditRequests,
    refreshing: refreshingCreditRequests,
  } = useGetCreditRequestBatches();

  // Context hooks for offline mode, layout, and user settings
  const isOfflineModeActive = useOfflineModeActive();
  const { isSmallLayout } = useLayoutContext();
  const {
    selectedRestaurantRelationship: { consumptionTax, displayItemPrices },
  } = useUserContext();
  const { useV2Invoices } = usePaymentsContext();
  const [splitContainerStyles] = useSplitContainerStyles();

  // State hooks for selected order, invoice, and order channel
  const [selectedOrderUUID, setSelectedOrderUUID] = useState<string | null>(
    null,
  );
  const [selectedInvoiceUUID, setSelectedInvoiceUUID] = useState<string | null>(
    null,
  );
  const [selectedCreditRequestUUID, setSelectedCreditRequestUUID] = useState<
    string | null
  >(null);
  const [selectedOrderHistoryFilter, setSelectedOrderHistoryFilter] = useState(
    FilterOption.Pepper,
  );

  // Refs to manage refresh and selection state
  const canRefreshOrdersRef = useRef(false);
  const hasInitialSelectedRef = useRef(false);

  // This ref will prevent the "reset to defaults" in onBlur callback in useComponentFocus when closing the parent invoice's modal
  const suppressNextBlurRef = useRef(false);

  const loading =
    loadingUpcomingOrders ||
    loadingPastOrders ||
    loadingInvoices ||
    loadingCreditRequests;

  const selectOrder = useCallback((uuid: string) => {
    setSelectedInvoiceUUID(null);
    setSelectedCreditRequestUUID(null);
    setSelectedOrderUUID(uuid);
  }, []);

  const selectInvoice = useCallback((uuid: string) => {
    setSelectedInvoiceUUID(uuid);
    setSelectedOrderUUID(null);
    setSelectedCreditRequestUUID(null);
  }, []);

  const selectCreditRequest = useCallback((uuid: string) => {
    setSelectedCreditRequestUUID(uuid);
    setSelectedInvoiceUUID(null);
    setSelectedOrderUUID(null);
  }, []);

  useEffect(() => {
    const preselectedOrderUUID = route.params.orderUUID;
    const preselectedInvoiceUUID = route.params.invoiceUUID;
    const preselectedCreditRequestUUID = route.params.creditRequestUUID;
    if (preselectedOrderUUID) {
      selectOrder(preselectedOrderUUID);
      hasInitialSelectedRef.current = true;
    } else if (preselectedInvoiceUUID) {
      selectInvoice(preselectedInvoiceUUID);
      setSelectedOrderHistoryFilter(FilterOption.External);
      hasInitialSelectedRef.current = true;
    } else if (preselectedCreditRequestUUID) {
      selectCreditRequest(preselectedCreditRequestUUID);
      setSelectedOrderHistoryFilter(FilterOption.CreditRequests);
      hasInitialSelectedRef.current = true;
    }
  }, [
    route.params.invoiceUUID,
    route.params.orderUUID,
    route.params.creditRequestUUID,
    selectOrder,
    selectInvoice,
    selectCreditRequest,
  ]);

  const ordersSections = useMemo(
    () =>
      [
        {
          data: upcomingOrders,
          key: i18n.t('upcoming'),
        },
        {
          data: pastOrders,
          key: i18n.t('past'),
        },
      ].filter((section) => section.data.length > 0),
    [upcomingOrders, pastOrders],
  );

  const invoicesSections = useMemo(
    () =>
      [
        {
          data: upcomingInvoices,
          key: i18n.t('upcoming'),
        },
        {
          data: pastInvoices,
          key: i18n.t('past'),
        },
      ].filter((section) => section.data.length > 0),
    [upcomingInvoices, pastInvoices],
  );

  const creditRequestsSections = useMemo(
    () =>
      [
        {
          data: creditRequests,
          key: i18n.t('past'),
        },
      ].filter((section) => section.data.length > 0),
    [creditRequests],
  );

  // Determine which sections to display based on selected order channel
  const dataSections =
    selectedOrderHistoryFilter === FilterOption.External
      ? invoicesSections
      : selectedOrderHistoryFilter === FilterOption.CreditRequests
        ? creditRequestsSections
        : ordersSections;

  useComponentFocus({
    onBlur: useCallback((focusSource: FocusSource) => {
      if (suppressNextBlurRef.current) {
        suppressNextBlurRef.current = false;
        return;
      }
      // Clear the selected order so the first one can be selected again
      if (focusSource === FocusSource.Navigation) {
        canRefreshOrdersRef.current = true;
        hasInitialSelectedRef.current = false;
        setSelectedOrderUUID(null);
        setSelectedInvoiceUUID(null);
        setSelectedCreditRequestUUID(null);
      }
    }, []),
    onFocus: useCallback(
      (focusSource: FocusSource) => {
        // Orders are already queried on component mount and shouldn't be
        // refreshed until after the screen is viewed for the first time
        if (
          focusSource === FocusSource.Navigation &&
          canRefreshOrdersRef.current
        ) {
          refreshUpcomingOrders();
          refreshPastOrders();
          refreshInvoices();
          refreshCreditRequests();
        }
      },
      [
        refreshCreditRequests,
        refreshInvoices,
        refreshPastOrders,
        refreshUpcomingOrders,
      ],
    ),
  });

  // Effect to select the first available order or invoice on initial load
  useEffect(() => {
    if (!loading && !hasInitialSelectedRef.current) {
      const orderToSelect =
        route.params.orderUUID ??
        (upcomingOrders.length
          ? upcomingOrders[0].value.toString()
          : pastOrders.length
            ? pastOrders[0].value.toString()
            : null);
      // Upcoming invoices are displayed first in the list
      const invoices = [...upcomingInvoices, ...pastInvoices];
      const invoiceToSelect = invoices.length
        ? invoices[0].value.toString()
        : null;

      if (orderToSelect) {
        selectOrder(orderToSelect);
      } else if (invoiceToSelect) {
        // If there are no orders, but there are invoices,
        // select the first invoice and flip the channel selector
        selectInvoice(invoiceToSelect);
        setSelectedOrderHistoryFilter(FilterOption.External);
      }

      hasInitialSelectedRef.current = true;
    }
  }, [
    loading,
    pastOrders,
    upcomingOrders,
    route.params.orderUUID,
    selectOrder,
    selectInvoice,
    pastInvoices,
    upcomingInvoices,
  ]);

  // Effect to switch to external channel if no orders exist but invoices do
  useEffect(() => {
    if (
      !loading &&
      pastOrders.length === 0 &&
      upcomingOrders.length === 0 &&
      pastInvoices.length > 0 &&
      upcomingInvoices.length > 0
    ) {
      setSelectedOrderHistoryFilter(FilterOption.External);
    }
  }, [
    loading,
    pastInvoices.length,
    pastOrders.length,
    upcomingInvoices.length,
    upcomingOrders.length,
  ]);

  const hasOrders = dataSections.some((section) => section.data.length > 0);

  const hasCreditRequests = creditRequests.length > 0;
  const hasExternalOrders =
    useV2Invoices && (pastInvoices.length > 0 || upcomingInvoices.length > 0);
  const hasPepperOrders =
    useV2Invoices && (pastOrders.length > 0 || upcomingOrders.length > 0);

  const availableSourcesCount = [
    hasPepperOrders,
    hasExternalOrders,
    hasCreditRequests,
  ].filter(Boolean).length;

  const showOrderHistoryFilters = availableSourcesCount > 1;

  return (
    <SplitPageContainer
      actions={[
        {
          component: <SubscriptionButton />,
        },
      ]}
      primaryComponent={
        loading &&
        !fetchingMorePastOrders &&
        !fetchingMoreInvoices &&
        !fetchingMoreCreditRequests &&
        !refreshingPastOrders &&
        !refreshingInvoices &&
        !refreshingCreditRequests &&
        !refreshingUpcomingOrders ? (
          <View style={splitContainerStyles.loadingContainer}>
            <ActivityIndicator />
          </View>
        ) : hasOrders ? (
          <OrderHistoryList
            consumptionTax={consumptionTax}
            displayItemPrices={displayItemPrices}
            extraData={{ selectedOrderUUID }}
            ListFooterComponent={
              <View style={splitContainerStyles.listFooter}>
                {fetchingMorePastOrders ? <ActivityIndicator /> : null}
              </View>
            }
            listItemContainerStyleFn={(option) => [
              splitContainerStyles.listItemContainer,
              option.value === selectedOrderUUID ||
              option.value === selectedInvoiceUUID ||
              option.value === selectedCreditRequestUUID
                ? splitContainerStyles.listItemContainerSelected
                : null,
            ]}
            onCreditRequestSelect={selectCreditRequest}
            onEndReached={() => {
              if (selectedOrderHistoryFilter === FilterOption.Pepper) {
                fetchMorePastOrders();
              } else if (selectedOrderHistoryFilter === FilterOption.External) {
                fetchMoreInvoices();
              } else if (
                selectedOrderHistoryFilter === FilterOption.CreditRequests
              ) {
                fetchMoreCreditRequests();
              }
            }}
            onInvoiceSelect={selectInvoice}
            onOrderSelect={selectOrder}
            refreshControl={
              <RefreshControl
                onRefresh={() => {
                  refreshPastOrders(true);
                  refreshInvoices(true);
                  refreshUpcomingOrders(true);
                  refreshCreditRequests(true);
                }}
                refreshing={
                  refreshingPastOrders ||
                  refreshingInvoices ||
                  refreshingUpcomingOrders ||
                  refreshingCreditRequests
                }
              />
            }
            // renderTarget parameter is unused here but required by the prop signature
            renderSectionHeader={(item, _, index) => (
              <View style={styles.listSectionHeader}>
                <Text style={[TYPOGRAPHY.h3, styles.listSectionHeading]}>
                  {item.title}
                </Text>
                {showOrderHistoryFilters && index === 0 ? (
                  <OrderHistoryFilters
                    hasCreditRequests={hasCreditRequests}
                    hasExternalOrders={hasExternalOrders}
                    hasPepperOrders={hasPepperOrders}
                    onFilterSelect={setSelectedOrderHistoryFilter}
                    selectedFilter={selectedOrderHistoryFilter}
                  />
                ) : null}
              </View>
            )}
            sectionHeaderHeight={LIST_SECTION_HEADER_HEIGHT}
            sections={dataSections}
            stickyHeaderIndices={[]}
          />
        ) : isOfflineModeActive ? null : (
          <>
            {showOrderHistoryFilters ? (
              <View style={styles.listSectionHeader}>
                <OrderHistoryFilters
                  hasCreditRequests={hasCreditRequests}
                  hasExternalOrders={hasExternalOrders}
                  hasPepperOrders={hasPepperOrders}
                  onFilterSelect={setSelectedOrderHistoryFilter}
                  selectedFilter={selectedOrderHistoryFilter}
                />
              </View>
            ) : null}
            <OrdersEmptyState
              onNewOrderPress={() => {
                navigation.navigate(APP_ROUTES.OrderGuide);
              }}
            />
          </>
        )
      }
      title={i18n.t('orders')}
    >
      {isOfflineModeActive ? (
        <OfflineComponent />
      ) : hasOrders ? (
        selectedOrderUUID ? (
          <OrderDetailComponent
            invoiceButtonPosition={isSmallLayout ? 'bottom' : 'top'}
            navigationHeaderContainerStyle={styles.navigationHeaderContainer}
            orderUUID={selectedOrderUUID}
          />
        ) : selectedInvoiceUUID ? (
          <InvoiceDetailComponent
            invoiceUUID={selectedInvoiceUUID}
            navigationHeaderContainerStyle={styles.navigationHeaderContainer}
            renderPayable={false}
          />
        ) : selectedCreditRequestUUID ? (
          <CreditRequestDetailComponent
            creditRequestBatchUUID={selectedCreditRequestUUID}
            navigationHeaderContainerStyle={styles.navigationHeaderContainer}
          />
        ) : null
      ) : null}
    </SplitPageContainer>
  );
};

const styles = StyleSheet.create({
  listSectionHeader: {
    paddingHorizontal: SPLIT_CONTAINER_MARGIN_HORIZONTAL,
    paddingVertical: 4,
  },
  listSectionHeading: {
    flex: 1,
    lineHeight: LIST_HEIGHT,
  },
  navigationHeaderContainer: {
    paddingHorizontal: SCREEN_PADDING,
    // The animated navigation header on native automatically includes padding
    // so only set it for web
    paddingTop: Platform.OS === 'web' ? 24 : 0,
  },
});
